import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import axiosInstance from "../../../api/axiosInstance";
import endpoints from "../../../api/endpoints";

/** Représentant, son état et les codes des sites Sage qui lui sont affectés (API Administration). */
export interface RepresentantSites {
  userId: string;
  username: string;
  businessId: string | null;
  isActive: boolean;
  preorderCount: number;
  sitesVente: string[];
  sitesExpedition: string[];
}

export interface SaveRepresentantSitesPayload {
  userId: string;
  sitesVente: string[];
  sitesExpedition: string[];
}

/** Représentant Sage (SALESREP) proposé pour choisir le code d'un nouveau compte. */
export interface SageRepresentant {
  code: string;
  nom: string | null;
  zone: string | null;
  secteur: string | null;
}

export interface CreateRepresentantPayload {
  username: string;
  password: string;
  email?: string;
  businessId: string;
  sitesVente: string[];
  sitesExpedition: string[];
}

export interface SetRepresentantStatusPayload {
  userId: string;
  isActive: boolean;
}

/** Erreur de suppression : `conflict` = données liées, l'archivage est proposé. */
export interface DeleteRepresentantError {
  message: string;
  conflict: boolean;
  preorderCount: number;
}

interface ApiEnvelope<T> {
  success: boolean;
  message: string;
  data: T;
}

interface RepresentantSitesState {
  representants: RepresentantSites[];
  loading: boolean;
  saving: boolean;
  creating: boolean;
  deleting: boolean;
  sageRepresentants: SageRepresentant[];
  sageLoading: boolean;
  error: string | null;
}

const initialState: RepresentantSitesState = {
  representants: [],
  loading: false,
  saving: false,
  creating: false,
  deleting: false,
  sageRepresentants: [],
  sageLoading: false,
  error: null,
};

const extractErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | (Partial<ApiEnvelope<unknown>> & { title?: string; errors?: Record<string, string[]> })
      | string
      | undefined;
    if (typeof data === "string" && data) return data;
    if (data && typeof data === "object") {
      if (typeof data.message === "string" && data.message) return data.message;
      // ProblemDetails de validation ASP.NET : { title, errors: { champ: [messages] } }
      if (data.errors && typeof data.errors === "object") {
        const messages = Object.values(data.errors).flat();
        if (messages.length > 0) return messages.join(" | ");
      }
      if (typeof data.title === "string" && data.title) return data.title;
    }
    if (error.response?.status === 403) return "Vous n'êtes pas autorisé";
  }
  return fallback;
};

export const fetchRepresentantSites = createAsyncThunk<
  RepresentantSites[],
  void,
  { rejectValue: string }
>("representantSites/fetch", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<ApiEnvelope<RepresentantSites[]>>(
      endpoints.administration.representants
    );
    if (!response.data.success) return rejectWithValue(response.data.message);
    return Array.isArray(response.data.data) ? response.data.data : [];
  } catch (error) {
    return rejectWithValue(extractErrorMessage(error, "Erreur de chargement des représentants"));
  }
});

export const saveRepresentantSites = createAsyncThunk<
  RepresentantSites,
  SaveRepresentantSitesPayload,
  { rejectValue: string }
>("representantSites/save", async (payload, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<ApiEnvelope<RepresentantSites>>(
      endpoints.administration.representantSites(payload.userId),
      { sitesVente: payload.sitesVente, sitesExpedition: payload.sitesExpedition }
    );
    if (!response.data.success) return rejectWithValue(response.data.message);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(extractErrorMessage(error, "Erreur lors de l'enregistrement des sites"));
  }
});

export const searchSageRepresentants = createAsyncThunk<
  SageRepresentant[],
  string,
  { rejectValue: string }
>("representantSites/searchSage", async (search, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<ApiEnvelope<SageRepresentant[]>>(
      endpoints.administration.sageRepresentants,
      { params: { search, page: 1 } }
    );
    if (!response.data.success) return rejectWithValue(response.data.message);
    return Array.isArray(response.data.data) ? response.data.data : [];
  } catch (error) {
    return rejectWithValue(extractErrorMessage(error, "Erreur de recherche dans Sage"));
  }
});

export const createRepresentant = createAsyncThunk<
  RepresentantSites,
  CreateRepresentantPayload,
  { rejectValue: string }
>("representantSites/create", async (payload, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<ApiEnvelope<RepresentantSites>>(
      endpoints.administration.representants,
      payload
    );
    if (!response.data.success) return rejectWithValue(response.data.message);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(extractErrorMessage(error, "Erreur lors de la création du représentant"));
  }
});

export const setRepresentantStatus = createAsyncThunk<
  RepresentantSites,
  SetRepresentantStatusPayload,
  { rejectValue: string }
>("representantSites/setStatus", async (payload, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<ApiEnvelope<RepresentantSites>>(
      endpoints.administration.representantStatus(payload.userId),
      { isActive: payload.isActive }
    );
    if (!response.data.success) return rejectWithValue(response.data.message);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(extractErrorMessage(error, "Erreur lors du changement de statut"));
  }
});

export const deleteRepresentant = createAsyncThunk<
  string,
  string,
  { rejectValue: DeleteRepresentantError }
>("representantSites/delete", async (userId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.delete<ApiEnvelope<{ userId: string }>>(
      endpoints.administration.representantSites(userId).replace(/\/sites$/, "")
    );
    if (!response.data.success) {
      return rejectWithValue({ message: response.data.message, conflict: false, preorderCount: 0 });
    }
    return userId;
  } catch (error) {
    const conflict = axios.isAxiosError(error) && error.response?.status === 409;
    const data = axios.isAxiosError(error)
      ? (error.response?.data as Partial<ApiEnvelope<{ preorderCount?: number }>> | undefined)
      : undefined;
    return rejectWithValue({
      message: extractErrorMessage(error, "Erreur lors de la suppression du représentant"),
      conflict,
      preorderCount: data?.data?.preorderCount ?? 0,
    });
  }
});

const upsert = (list: RepresentantSites[], item: RepresentantSites) => {
  const index = list.findIndex((r) => r.userId === item.userId);
  if (index >= 0) {
    list[index] = item;
  } else {
    list.push(item);
    list.sort((a, b) => a.username.localeCompare(b.username));
  }
};

const representantSitesSlice = createSlice({
  name: "representantSites",
  initialState,
  reducers: {
    clearSageRepresentants(state) {
      state.sageRepresentants = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRepresentantSites.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRepresentantSites.fulfilled, (state, action) => {
        state.loading = false;
        state.representants = action.payload;
      })
      .addCase(fetchRepresentantSites.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message || "Erreur de chargement";
      })
      .addCase(saveRepresentantSites.pending, (state) => {
        state.saving = true;
      })
      .addCase(saveRepresentantSites.fulfilled, (state, action) => {
        state.saving = false;
        upsert(state.representants, action.payload);
      })
      .addCase(saveRepresentantSites.rejected, (state) => {
        state.saving = false;
      })
      .addCase(searchSageRepresentants.pending, (state) => {
        state.sageLoading = true;
      })
      .addCase(searchSageRepresentants.fulfilled, (state, action) => {
        state.sageLoading = false;
        state.sageRepresentants = action.payload;
      })
      .addCase(searchSageRepresentants.rejected, (state) => {
        state.sageLoading = false;
        state.sageRepresentants = [];
      })
      .addCase(createRepresentant.pending, (state) => {
        state.creating = true;
      })
      .addCase(createRepresentant.fulfilled, (state, action) => {
        state.creating = false;
        upsert(state.representants, action.payload);
      })
      .addCase(createRepresentant.rejected, (state) => {
        state.creating = false;
      })
      .addCase(setRepresentantStatus.pending, (state) => {
        state.saving = true;
      })
      .addCase(setRepresentantStatus.fulfilled, (state, action) => {
        state.saving = false;
        upsert(state.representants, action.payload);
      })
      .addCase(setRepresentantStatus.rejected, (state) => {
        state.saving = false;
      })
      .addCase(deleteRepresentant.pending, (state) => {
        state.deleting = true;
      })
      .addCase(deleteRepresentant.fulfilled, (state, action) => {
        state.deleting = false;
        state.representants = state.representants.filter((r) => r.userId !== action.payload);
      })
      .addCase(deleteRepresentant.rejected, (state) => {
        state.deleting = false;
      });
  },
});

export const { clearSageRepresentants } = representantSitesSlice.actions;

export default representantSitesSlice.reducer;
